/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useState } from 'react';
import * as Ron from 'blb-ui-types/ron';
import { Box } from 'grommet';
import { UploadFileFn } from '@blend/enterprise-components';
import { Form } from 'react-final-form';
import * as Sentry from '@sentry/browser';
import { FormApi } from 'final-form';
import axios from 'axios';
import { get } from 'lodash';
import { DateTime } from 'luxon';

import { UserFileUploader } from 'blb-components/AuthenticatedApp/ProfilePage/UserFileUploader';
import { uploadFile } from 'blb-api/files';
import { BlendBridge } from 'blb-types/blendBridge';
import { CredentialTypeNames, ImageExtensions, UserCredentialType } from 'blb-constants/ron';
import { downloadCredentialFileForCurrentUser, patchCredential } from 'blb-api/ron';
import { CredentialViewer } from 'blb-components/AuthenticatedApp/RON/CredentialViewer/CredentialViewer';
import { DigitalCertificateMeta, UserCredentialMeta } from 'blb-ui-types/ronMeta';
import { FullScreenModal } from 'blb-components/GrommetComponents';
import { uploadKey } from 'blb-api/keys';
import {
  formatCredentialDate,
  formatCredentialMeta,
  normalizeCredentialMeta,
} from 'blb-components/AuthenticatedApp/RON/CredentialInfo/transformCredentialMeta';
import { AttributeEditor } from 'blb-components/AuthenticatedApp/RON/CredentialInfo/AttributeEditor';
import { EditCredentialFormValues } from 'blb-components/AuthenticatedApp/RON/CredentialInfo/types';
import { styledAttrs } from 'blb-utils/misc';
import { DATE_ISO_FORMAT, DATE_US_FORMAT } from 'blb-components/AuthenticatedApp/RON/CredentialInfo/dateUtils';
import { createP12Service } from 'blb-components/AuthenticatedApp/RON/CredentialInfo/p12Service';
import { CredentialPasswordModal } from 'blb-components/AuthenticatedApp/RON/CredentialInfo/CredentialPasswordModal';

export namespace EditCredentialModal {
  export interface Props {
    credential?: Ron.API.UserCredential;
    credentialType: UserCredentialType;
    handleClose: () => void;
  }
}

const AttributeEditorWrapper = styledAttrs(Box, {
  background: 'white',
  basis: '1/3',
  direction: 'column',
  elevation: 'small',
  flex: { grow: 0, shrink: 1 },
  justify: 'stretch',
  margin: 'medium',
  round: 'xxsmall',
});

const CredentialViewerWrapper = styledAttrs(Box, {
  align: 'stretch',
  elevation: 'small',
  flex: { grow: 1, shrink: 3 },
  overflow: { vertical: 'scroll' },
});

const FormWrapper = styledAttrs(Box, {
  align: 'stretch',
  background: 'backgroundGrey',
  direction: 'row',
  fill: true,
  margin: { top: 'hair' },
});

export const EditCredentialModal: React.FC<EditCredentialModal.Props> = ({
  credential,
  credentialType,
  handleClose,
}) => {
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [isSavingChanges, setIsSavingChange] = useState(false);
  const [isCredentialPasswordModalOpen, setIsCredentialPasswordModalOpen] = useState(false);
  const [error, setError] = useState<string>();
  const [file, setFile] = useState<File>();
  const [digitalCertificateMeta, setDigitalCertificateMeta] = useState<DigitalCertificateMeta>();
  const isDigitalCertificate = credentialType === UserCredentialType.DIGITAL_CERTIFICATE;

  useEffect(() => {
    const downloadCredentialFile = async (credentialId: string) => {
      try {
        setIsLoadingFile(true);
        const blob = await downloadCredentialFileForCurrentUser(credentialId);
        // old credentials does not have names and they are all in pdf
        setFile(new File([blob], credential?.fileName || 'file.pdf'));
      } catch (error) {
        Sentry.captureException(error);
        setError(get(error, 'message'));
      } finally {
        setIsLoadingFile(false);
      }
    };

    if (credential) {
      if (isDigitalCertificate) {
        const meta = credential.meta as DigitalCertificateMeta;
        setDigitalCertificateMeta({
          expirationDate: meta.expirationDate,
          issuer: meta.issuer,
          subject: meta.subject,
        });
      } else {
        downloadCredentialFile(credential.id);
      }
    }
  }, [credential, isDigitalCertificate]);

  const uploadCertificateToUserKeyStore = async (form: FormApi<EditCredentialFormValues>, file: File) => {
    const result = await uploadKey(file);
    const { issuer, subject, expiration, id, key, fullPath } = result.data;
    const newMeta = { expirationDate: DateTime.fromSeconds(expiration).toFormat(DATE_ISO_FORMAT), issuer, subject };
    setDigitalCertificateMeta(newMeta);

    // update form values for digital certificate. These fields are disabled
    form.change('issuer', newMeta.issuer);
    form.change('subject', newMeta.subject);
    form.change('expirationDate', formatCredentialDate(newMeta.expirationDate, DATE_ISO_FORMAT, DATE_US_FORMAT));
    form.change('fileId', result.data.key);
    form.change('fileName', file.name);
    return {
      fullPath,
      id,
      key,
    };
  };

  const handleUploadDigitalCertificate = async (
    form: FormApi<EditCredentialFormValues>,
    params: Parameters<UploadFileFn<{ fileType: BlendBridge.FileTypes }>>[0],
  ) => {
    try {
      // It's error-prone to verify if a p12 does not have password. No password can mean: empty-string or undefined password
      // Depending on the encryption algorithm, different error message can be thrown
      // (ex: TripleDES-SHA1 accepts both empty-string and undefined password, but AES256-SHA256 does not
      // accept 'undefined' password and it throws a runtime exception )
      // For safety, first upload it to userKeyStore. If fails, then ask user to input password
      return { data: await uploadCertificateToUserKeyStore(form, params.file) };
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 400) {
        const p12Service = await createP12Service();
        const decrypt = await p12Service.decryptP12File(params.file, '');
        if (!decrypt.isValidPassword) {
          setIsCredentialPasswordModalOpen(true);
          // return empty key to clear to form data. This disables the submit button
          return {
            data: {
              fullPath: '',
              id: '',
              key: '',
            },
          };
        }
      }
      throw err;
    }
  };

  const handleUploadFile = async (
    form: FormApi<EditCredentialFormValues>,
    params: Parameters<UploadFileFn<{ fileType: BlendBridge.FileTypes }>>[0],
  ) => {
    try {
      setIsLoadingFile(true);
      setFile(params.file);
      if (isDigitalCertificate) {
        return await handleUploadDigitalCertificate(form, params);
      } else {
        const result = await uploadFile(params);
        form.change('fileName', params.file?.name);
        return result;
      }
    } catch (err) {
      Sentry.captureException(err);
      setError(get(err, 'response.data.error.message', 'Failed to upload file'));
      throw err;
    } finally {
      setIsLoadingFile(false);
    }
  };

  const handleSubmitCredentialPassword = async (
    form: FormApi<EditCredentialFormValues>,
    password: string,
  ): Promise<boolean> => {
    if (!file) {
      throw new Error('File is not defined');
    }
    const certificateService = await createP12Service();
    const decrypt = await certificateService.decryptP12File(file, password);
    if (!decrypt.isValidPassword) {
      return false;
    }

    const noPasswordP12Buffer = certificateService.generateP12NoPassword(decrypt.p12);
    const noPasswordP12File = new File([noPasswordP12Buffer], file.name, {
      type: file.type,
      lastModified: file.lastModified,
    });
    await uploadCertificateToUserKeyStore(form, noPasswordP12File);
    return true;
  };

  const renderUploadDocumentLink = (smallSize: boolean, form: FormApi<EditCredentialFormValues>, reupload: boolean) => {
    const attrs =
      credentialType === UserCredentialType.DIGITAL_CERTIFICATE
        ? { acceptFileExts: '.p12', fileType: BlendBridge.FileTypes.NOTARY_DIGITAL_CERTIFICATE }
        : { acceptFileExts: ['.pdf', ...ImageExtensions].join(','), fileType: BlendBridge.FileTypes.NOTARY_COMMISSION };
    return (
      <UserFileUploader
        {...attrs}
        disabled={false}
        form={form}
        handleUploadEvent={() => {}}
        id="it-FileUploader"
        linkButtonProps={{
          btnSize: smallSize ? 'sm' : 'md',
          icon: 'file_upload',
          label: reupload ? 'Re-upload file' : 'Upload file',
        }}
        uploadFile={params => handleUploadFile(form, params)}
      />
    );
  };

  const getTitle = () => {
    return `${credential ? 'Edit ' : 'Add '} ${CredentialTypeNames[credentialType]}`;
  };

  const formInitialValues: EditCredentialFormValues = {
    ...formatCredentialMeta(credential?.meta),
    fileId: credential?.fileId,
    fileName: credential?.fileName,
  };

  const onSaveChange = async (values: EditCredentialFormValues) => {
    try {
      setIsSavingChange(true);
      const { fileId, fileName, ...meta } = values;
      const normalizedMeta = isDigitalCertificate ? undefined : normalizeCredentialMeta(meta as UserCredentialMeta);
      await patchCredential({
        credentialType,
        // there is a fileIdValidator to ensure fileId is not undefined
        fileId: fileId!,
        fileName: fileName!,
        meta: normalizedMeta as UserCredentialMeta,
      });
      handleClose();
    } catch (error) {
      Sentry.captureException(error);
      setError('Failed to update credential');
      throw error;
    } finally {
      setIsSavingChange(false);
    }
  };

  const fileValidator = (values: EditCredentialFormValues) => (values.fileId ? {} : 'File was not uploaded');

  const formElement = (
    <Form initialValues={formInitialValues} onSubmit={onSaveChange} validate={fileValidator}>
      {({ form, values, hasValidationErrors, handleSubmit, dirty }) => {
        return (
          <>
            {isCredentialPasswordModalOpen && (
              <CredentialPasswordModal
                handleClose={() => setIsCredentialPasswordModalOpen(false)}
                handleSubmit={password => handleSubmitCredentialPassword(form, password)}
              />
            )}
            <CredentialViewerWrapper>
              <CredentialViewer
                certificateMetaData={digitalCertificateMeta}
                credentialType={credentialType}
                error={error}
                file={file}
                isLoading={isLoadingFile}
                uploadDocumentLink={renderUploadDocumentLink(false, form, !!values.fileId)}
              />
            </CredentialViewerWrapper>
            <AttributeEditorWrapper id="it-attributeEditor-holder">
              <AttributeEditor
                title={getTitle()}
                uploadLink={renderUploadDocumentLink(true, form, !!values.fileId)}
                {...{
                  credentialType,
                  formValues: values,
                  handleSubmit,
                  hasValidationErrors,
                  isDirty: dirty,
                  isSavingChanges,
                }}
              />
            </AttributeEditorWrapper>
          </>
        );
      }}
    </Form>
  );

  return (
    <FullScreenModal handleClose={handleClose} id="editCredentialModal" title={getTitle()}>
      <FormWrapper>{formElement}</FormWrapper>
    </FullScreenModal>
  );
};
